import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { type SenderAvailabilityQuery } from 'src/api/generated';
import { useSupportTenant } from 'src/modules/shared/contexts/support-tenant-context';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { senderAvailabilityQueryOptions } from 'src/modules/shared/utils/api/sender-availability-query.util';

/**
 * Tenant- and sender-scoped connection availability.
 * @param params - Channel and sender address from the generated API contract.
 * @param enabled - Whether a real sender needs checking (false for simulations).
 * @returns Connection evidence and explicit loading/error state; not message eligibility.
 */
export const useSenderAvailability = (
  params: SenderAvailabilityQuery,
  enabled = true
) => {
  const { assumedTenantId } = useSupportTenant();
  const { user } = useCurrentUser();
  const tenantId = assumedTenantId ?? user?.tenantId ?? null;
  const query = useQuery(
    senderAvailabilityQueryOptions(tenantId, params, enabled)
  );
  return { ...query, loading: query.isPending || query.isFetching };
};

/**
 * Checks distinct sender choices with the same permissions and cache as send guards.
 * @param params - Distinct sender identities to check.
 * @param enabled - False for synthetic test conversations.
 * @returns Availability evidence scoped to the effective tenant for every sender.
 */
export const useSenderAvailabilities = (
  params: SenderAvailabilityQuery[],
  enabled = true
) => {
  const { assumedTenantId } = useSupportTenant();
  const { user } = useCurrentUser();
  const tenantId = assumedTenantId ?? user?.tenantId ?? null;
  const queries = useMemo(
    () =>
      params.map((sender) =>
        senderAvailabilityQueryOptions(tenantId, sender, enabled)
      ),
    [params, tenantId, enabled]
  );

  return useQueries({ queries });
};
